import QueryString from 'query-string';
import _ from 'lodash';
import {sprintf} from 'sprintf-js';
import { HTTPError} from './errors';
import Logger, {LOG_LEVELS, ALL_MESSAGES} from './logger';
import fetch from '../utils/fetch-wrapper';
import getClientConfig from './getClientConfig';

const log = new Logger('API_ENDPOINTS', LOG_LEVELS.DEBUG);
const { vrRestApiHost } = getClientConfig();

const errorHandler = (response, handle401 = true) => {
  if (!response.ok) {
      if([500, 503, 504].includes(response.status) || !response.status) {
        log.setDictionaryMessage(ALL_MESSAGES.CONNECTION_ERROR);
      }
      // On session expired/logged out, redirect user to login page.
      if(response.status === 401 && handle401 === true)
      {
          //redirect to login page, this.props.history.push is not working since prop is undefined
          window.open("/","_self");
      }
      throw new HTTPError(response.status, response.statusText);
  }
  return response;
}

const APIHelper = (function() {
    let api = {};
    api.host = Object.freeze((vrRestApiHost) || '');
    api.getURL = (relativeURL='', queryParams={}, urlParams={}) => {
      let queryParamsStr = _.isEmpty(queryParams) ? '' : `?${QueryString.stringify(queryParams)}`;
      let relativeURLWithParams;
      if(_.isEmpty(urlParams)) {
        relativeURLWithParams = relativeURL;
      } else {
        let encodedUrlParams = JSON.parse(JSON.stringify(urlParams, (key, value) =>  _.isEmpty(key) ? value: encodeURIComponent(value)));
        log.debug('APIHelper.getURL(): encodedUrlParams: ', encodedUrlParams);
        relativeURLWithParams = sprintf(relativeURL, encodedUrlParams);
      }
      log.debug('APIHelper.getURL(): queryParamsStr: ', queryParamsStr);
      log.debug('APIHelper.getURL(): relativeURLWithParams: ', relativeURLWithParams);
      return `${api.host}${relativeURLWithParams}${queryParamsStr}` ;
    };

    //----------------------
    // Common HTTP methods
    //-----------------------
    //Get binary content
    api.getBlob = (uri, token, mimeType, onSuccess, onError) => {
      onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
      onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
      return new Promise((resolve, reject) => {
          return fetch(uri, {
              method: 'GET',
              mode: 'cors',
              headers: {
                  'content-type': mimeType
              },
              cache: 'no-cache'
          })
              .then(errorHandler)
              .then((response) => {
                  return response.blob();
              })
              .then((blob) => {
                  log.debug('getBlob: success: ', uri, blob);
                  onSuccess(blob, resolve, reject);
              })
              .catch((error) => {
                  log.debug('getBlob: error:', uri, error);
                  onError(error, resolve, reject);
              });
      });
    }

    //HTTP Get Request
    api.get = (uri, token, onSuccess, onError, fullResponse=false, headers = {}, handle401 = true) => {
        onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
        onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
        let statusCode;

        return new Promise((resolve, reject) => {
            return fetch(uri, {
                headers,
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache'
            })
                .then((response) => errorHandler(response, handle401))
                .then((response) => {
                    statusCode = response.status;
                    if(fullResponse) {
                      return response;
                    }
                    else {
                      return response.json();
                    }
                })
                .then((data) => {
                    log.debug('get: success: ', uri, data);
                    onSuccess(data, resolve, reject);
                })
                .catch((error) => {
                   //response.json will throw an error in case empty response is sent from the API
                   if(statusCode >= 200 && statusCode < 300) {
                    onSuccess({}, resolve, reject);
                  } else {
                    log.debug('get: error:', uri, error);
                    onError(error, resolve, reject);
                  }
                });
        });
    }

    //HTTP Temp Dummy Request for Correctives modal
    api.dummy = (uri, token, onSuccess, onError, fullResponse=false, headers = {}) => {
      onSuccess =
        typeof onSuccess === "function"
          ? onSuccess
          : (json, resolve, reject) => resolve(json);

      const dummyData = [
        {
          id: 0,
          userId: 147,
          name: "BELTRADING NV",
          country: "BE",
          vatReturnStartDate: "2022-04-01T00:00:00",
          vatReturnEndDate: "2022-06-30T00:00:00",
          dueDate: "2022-06-30T00:00:00",
          daysUntilDue: 4,
          returnType: "vat",
          status: "Status: ready",
          fileName: "123.xml",
          periodicity: 1
        },
        {
          id: 24,
          userId: 148,
          name: "BELTRADING NV",
          country: "BE",
          vatReturnStartDate: "2022-04-01T00:00:00",
          vatReturnEndDate: "2022-06-30T00:00:00",
          dueDate: "2022-06-30T00:00:00",
          daysUntilDue: 5,
          returnType: "eclisting",
          status: "Status: ready",
          fileName: "123.xml",
          periodicity: 1
        },
        {
          id: 48,
          userId: 149,
          name: "BELTRADING NV",
          country: "BE",
          vatReturnStartDate: "2022-04-01T00:00:00",
          vatReturnEndDate: "2022-06-30T00:00:00",
          dueDate: "2022-06-30T00:00:00",
          daysUntilDue: 6,
          returnType: "extrastat",
          status: "Status: ready",
          fileName: "123.xml",
          periodicity: 1
        },
        {
          id: 96,
          userId: 150,
          name: "BELTRADING NV",
          country: "BE",
          vatReturnStartDate: "2022-04-01T00:00:00",
          vatReturnEndDate: "2022-06-30T00:00:00",
          dueDate: "2022-06-30T00:00:00",
          daysUntilDue: 7,
          returnType: "intrastat",
          status: "Status: ready",
          fileName: "123.xml",
          periodicity: 1
        },
        {
          id: 192,
          userId: 116,
          name: "BELTRADING NV",
          country: "BE",
          vatReturnStartDate: "2022-09-01T00:00:00",
          vatReturnEndDate: "2022-09-30T00:00:00",
          dueDate: "2022-06-30T00:00:00",
          daysUntilDue: 7,
          returnType: "vat",
          status: "Status: ready",
          fileName: "6330ab98-d17f-4489-9204-d860cd1e3e82.xml",
          periodicity: 1
        },
      ];

      return new Promise((resolve, reject) => {
        return onSuccess(dummyData, resolve, reject);
      });
  }

    //HTTP Post Request
    api.post = (uri, token, body, onSuccess, onError, rawData=false) => {
        onSuccess = typeof onSuccess === 'function' ? onSuccess : (json, resolve, reject) => resolve(json);
        onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
        let statusCode;
        let headers = {}
        if(!rawData) {
          headers['Content-Type'] = 'application/json';
        }
        return new Promise((resolve, reject) => {

            return fetch(uri, {
                method: 'POST',
                mode: 'cors',
                headers: headers,
                body: rawData ? body: JSON.stringify(body),
                cache: 'no-cache'
            })
                .then(errorHandler)
                .then((response) => {
                  statusCode = response.status;
                  return response.json();
                })
                .then((json) => {
                    log.debug('post: success: ', uri, json);
                    onSuccess(json, resolve, reject);
                })
                .catch((error) => {
                    //response.json will throw an error in case empty response is sent from the API
                    if(statusCode >= 200 && statusCode < 300) {
                      onSuccess({}, resolve, reject);
                    } else {
                      log.debug('post: error:', uri, error);
                      onError(error, resolve, reject);
                    }
                });
        });
    }

    //HTTP Put Request
    api.put = (uri, token, body, onSuccess, onError) => {
      onSuccess = typeof onSuccess === 'function' ? onSuccess : (response, resolve, reject) => resolve(response);
      onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
      let statusCode;
      return new Promise((resolve, reject) => {
        return fetch(uri, {
          method: 'PUT',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          cache: 'no-cache',
          body: JSON.stringify(body)
        })
          .then(errorHandler)
          .then((response) => {
            statusCode = response.status;
            return response.json();
          })

          .catch((error) => {
            if (statusCode >= 200 && statusCode < 300) {
              onSuccess({}, resolve, reject);
            } else {
              log.debug('put: error:', uri, error);
              onError(error, resolve, reject);
            }
          });
      });
    }

    api.deletePromise = (uri, token, onSuccess, onError) => {
        onSuccess = typeof onSuccess === 'function' ? onSuccess : (response, resolve, reject) => resolve(response);
        onError = typeof onError === 'function' ? onError : (error, resolve, reject) => reject(error);
        let statusCode;
        return new Promise((resolve, reject) => {
            return fetch(uri, {
                method: 'DELETE',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache'
            })
                .then((response) => {
                    statusCode = response.status;
                    return response.json();
                })
                .then((json) => {
                    log.debug('delete: success: ', uri, json);
                    onSuccess(json, resolve, reject);
                })
                .catch((error) => {
                    //response.json will throw an error in case empty response is sent from the API
                    if(statusCode >= 200 && statusCode < 300) {
                      onSuccess({}, resolve, reject);
                    } else {
                      log.debug('delete: error:', uri, error);
                      onError(error, resolve, reject);
                    }
                });
        });
    }

    return api;
  })();

const FileValidationHelper = (function() {
  let validationHelper = {};

  validationHelper.getFileValidationMessage = (validationMessageCode) => {
    if (validationMessageCode.indexOf("ValidationError01") >= 0) {
      return ALL_MESSAGES.FILE_IMPORT_ERROR_EXTENSION_FORBIDDEN;
    } else if (validationMessageCode.indexOf("ValidationError02") >= 0) {
        return ALL_MESSAGES.FILE_IMPORT_ERROR_EXTENSION_TOO_LONG;
    } else if (validationMessageCode.indexOf("ValidationError03") >= 0) {
        return ALL_MESSAGES.FILE_IMPORT_ERROR_FILENAME_TOO_LONG;
    } else if (validationMessageCode.indexOf("ValidationError04") >= 0) {
        return ALL_MESSAGES.FILE_IMPORT_ERROR_FILENAME_FORBIDDEN;
    } else if (validationMessageCode.indexOf("ValidationError05") >= 0) {
        return ALL_MESSAGES.FILE_IMPORT_ERROR_CONTENTTYPE_FORBIDDEN;
    } else if (validationMessageCode.indexOf("ValidationError06") >= 0) {
        return ALL_MESSAGES.FILE_IMPORT_ERROR_CONTENTTYPE_MISMATCH;
    }
  };

  return validationHelper;
})();

const EmailAddressHelper = (function() {
  let validateEmailAddress = {};

  validateEmailAddress.validEmailAddress = (emailAddress) => {
    const regexRules = /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

    if (!emailAddress) return false;
    if (emailAddress > 254) return false;
    return (regexRules.test(emailAddress));
  }

  return validateEmailAddress;
})();

const DateHelper = (function () {
  let dateHelper = {};

  dateHelper.getStartDateOfCurrentMonth = () => {
    const currentDate = new Date();
    currentDate.setDate(1); // Set the day to 1

    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const day = currentDate.getDate().toString().padStart(2, '0');

    return`${year}-${month}-${day}`;
  };
  
  dateHelper.getEndDateOfCurrentMonth = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // Month is zero-based, so we add 1
    const lastDay = new Date(year, month, 0).getDate(); // Get the last day of the current month

    return `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
  };

  return dateHelper;
})();

export  { APIHelper, FileValidationHelper, EmailAddressHelper, DateHelper };
